/** Shared option lists, labels and row-field rules for assignment templates. */
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { AssignmentTaxonomyLabels, CurriculumModuleOption } from './interfaces'
import type { DisciplineCode } from './types'

import { AppRoute } from '@shared/config'

import {
  AssignmentStatus,
  AssignmentType,
  MathExerciseType,
} from './enums'

/** A union in lib, so there is no enum to iterate. */
export const DISCIPLINE_CODES: readonly DisciplineCode[] = ['MATH', 'ELA'] as const

const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
  [AssignmentType.Reading]: 'Reading',
  [AssignmentType.Math]: 'Math',
}

/** Both families have an editor: READING a chapter book, MATH an exercise set (RND-196). A type the
 *  API may add later stays unauthorable until it has one. */
const AUTHORABLE_ASSIGNMENT_TYPES: readonly AssignmentType[] = [AssignmentType.Reading, AssignmentType.Math]

const DISCIPLINE_CODE_LABELS: Record<DisciplineCode, string> = {
  MATH: 'Math',
  ELA: 'ELA',
}

const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
  [AssignmentStatus.Draft]: 'Draft',
  [AssignmentStatus.Published]: 'Published',
  [AssignmentStatus.Archived]: 'Archived',
}

const ASSIGNMENT_STATUS_COLORS: Record<AssignmentStatus, AppBadgeColor> = {
  [AssignmentStatus.Draft]: 'slate',
  [AssignmentStatus.Published]: 'emerald',
  [AssignmentStatus.Archived]: 'orange',
}

/** Discipline is derived from the type, never asked for separately. */
const ASSIGNMENT_TYPE_DISCIPLINES: Record<AssignmentType, DisciplineCode> = {
  [AssignmentType.Reading]: 'ELA',
  [AssignmentType.Math]: 'MATH',
}

const MATH_EXERCISE_TYPE_LABELS: Record<MathExerciseType, string> = {
  [MathExerciseType.MultipleChoice]: 'Multiple choice',
  [MathExerciseType.Numeric]: 'Numeric answer',
  [MathExerciseType.MatchPairs]: 'Match pairs',
  [MathExerciseType.Order]: 'Put in order',
  [MathExerciseType.NumberLine]: 'Number line',
  [MathExerciseType.MultiStep]: 'Multi-step',
}

export const getAssignmentTypeLabel = (value: AssignmentType): string =>
  ASSIGNMENT_TYPE_LABELS[value] ?? value

export const isAssignmentTypeAuthorable = (value: AssignmentType): boolean =>
  AUTHORABLE_ASSIGNMENT_TYPES.includes(value)

export const getDisciplineCodeForType = (value: AssignmentType): DisciplineCode =>
  ASSIGNMENT_TYPE_DISCIPLINES[value]

export const getDisciplineCodeLabel = (value: DisciplineCode): string =>
  DISCIPLINE_CODE_LABELS[value] ?? value

export const getAssignmentStatusLabel = (value: AssignmentStatus): string =>
  ASSIGNMENT_STATUS_LABELS[value] ?? value

export const getAssignmentStatusColor = (value: AssignmentStatus): AppBadgeColor =>
  ASSIGNMENT_STATUS_COLORS[value] ?? 'slate'

export const getMathExerciseTypeLabel = (value: MathExerciseType): string =>
  MATH_EXERCISE_TYPE_LABELS[value] ?? String(value)

export const assignmentTypeOptions: AppSelectOption<AssignmentType>[] = Object.values(AssignmentType)
  .map(value => ({ value, text: getAssignmentTypeLabel(value) }))

export const disciplineCodeOptions: AppSelectOption<DisciplineCode>[] = DISCIPLINE_CODES
  .map(value => ({ value, text: getDisciplineCodeLabel(value) }))

// ---- taxonomy label -------------------------------------------

/** `4. Story Elements › Character Traits › 3. Predicting…`, from whatever labels the row carries.
 *  `null` means no topic — render an explicit "No topic" state. */
export const getTaxonomyLabel = (row: AssignmentTaxonomyLabels): string | null => {
  if (row.curriculum_topic_id === null) return null

  const modulePart = [row.module_number === null ? null : `${row.module_number}.`, row.module_name]
    .filter((part): part is string => !!part)
    .join(' ')

  const lessonPart = row.curriculum_lesson_id === null
    ? null
    : [row.lesson_number === null ? null : `${row.lesson_number}.`, row.lesson_name]
      .filter((part): part is string => !!part)
      .join(' ')

  const parts = [modulePart, row.topic_name, lessonPart].filter((part): part is string => !!part)

  return parts.length ? parts.join(' › ') : null
}

/** `M.4 L.3` — module and lesson numbers; the lesson title renders on its own line and topics mostly
 *  repeat the module, so the full label goes into a tooltip. Falls back to the topic name when the row carries no module number, and to
 *  `null` when there is no topic at all. */
export const getTaxonomyShortLabel = (row: AssignmentTaxonomyLabels): string | null => {
  if (row.curriculum_topic_id === null) return null

  if (row.module_number === null) return row.topic_name || null

  const parts = [`M.${row.module_number}`]

  if (row.curriculum_lesson_id !== null && row.lesson_number !== null) {
    parts.push(`L.${row.lesson_number}`)
  }

  return parts.join(' ')
}

/** `4. Story Elements` — the module select in the list filters and the editor picker. */
export const getModuleOptions = (modules: CurriculumModuleOption[]): AppSelectOption<number>[] =>
  modules.map(module => ({
    value: module.id,
    text: `${module.module_number}. ${module.module_name}`,
  }))

export const STALE_HINT
  = 'The curriculum has moved on since this template was tagged. Open it and pick the topic again, '
  + 'or clear it.'

// ---- routes and validation -------------------------------------------------

/** `AppRoute.AssignmentEdit` is the `[id]` pattern and cannot be pushed as-is. */
export const getAssignmentEditPath = (templateId: number | string): string =>
  `${AppRoute.Assignments}/${templateId}`

export const TEMPLATE_TITLE_MAX_LENGTH = 255

export const isTemplateTitleValid = (title: string): boolean => {
  const trimmed = title.trim()

  return trimmed.length > 0 && trimmed.length <= TEMPLATE_TITLE_MAX_LENGTH
}
